import { useState, useRef, useEffect } from "react";
import SignatureCanvas from "react-signature-canvas";
import html2canvas from "html2canvas";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, PenTool, Send, Loader2, X } from "lucide-react";
import TextInput from "./Forms/TextInput";
import SelectInput from "./Forms/SelectInput";
import { ReduxUpdater } from "../../../redux/ReduxUpdater";
import { selectedYachtCurrenciesState, selectedYachtState } from "../../../redux/slices/yachtSlice";
import { useSelector } from "react-redux";
import DateTimeInput, { formatReceiptDate } from "./Forms/DateTimeInput";
import { ApiCalls, IReceiptData, IYachtData } from "../../../ApiCalls";
import { selectedUserState } from "../../../redux/slices/userSlice";
import { toast } from "react-toastify";
import { GlobalVariables } from "../../../GlobalVariables";
import { GlobalConstants } from "../../../GlobalConstants";
import { GlobalPermissions } from "../../../GlobalPermissions";

interface ICreateReceiptProps {
    initData?: IReceiptData;
    showCancel?: boolean;
    isPettyCash: boolean;
    onError?: () => void;
    onSuccess?: () => void;
}

const CreateReceipt = (props: ICreateReceiptProps) => {
    const currentSelectedUserState = useSelector(selectedUserState);
    const currentSelectedYachtState = useSelector(selectedYachtState);
    const currentSelectedYachtCurrencyState = useSelector(selectedYachtCurrenciesState);

    const { showCancel, initData, isPettyCash, onError, onSuccess } = props;

    console.log('CreateReceipt:', GlobalVariables.manualReceipt);

    const emptyReceipt = {
        message_id: `RCPT.${currentSelectedYachtState?.name}.${String(currentSelectedUserState?.id).padStart(5, '0')}.date-${new Date().toISOString()}`,
        transaction_date: formatReceiptDate(new Date()),
        transaction_time: null,
        amount: "",
        currency: currentSelectedYachtCurrencyState[0],
        vendor_name: "",
        maris_category_id: -1,
        general_item_description: "Maris App Generated receipt",
        user_description: "",
        user_id: currentSelectedUserState?.id ?? -1,
        receipt_type: isPettyCash ? 'petty_cash' : null,
    } as IReceiptData;

    const [step, setStep] = useState(1);
    const [lastYachtRefreshTS, setLastYachtRefreshTS] = useState(Date.now());
    const [receiptNumber, setReceiptNumber] = useState(currentSelectedYachtState?.next_receipt ?? 1);
    const [receiptData, setData] = useState<IReceiptData>({
        ...(initData ?? emptyReceipt),
        receipt_type: isPettyCash ? 'petty_cash' : null
    });
    const [signature, setSignature] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [sendStatus, setSendStatus] = useState<"idle" | "success" | "error">("idle");

    const sigCanvas = useRef<SignatureCanvas>(null);
    const receiptRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!currentSelectedYachtState) {
            return;
        }
        ApiCalls.yacht(currentSelectedYachtState.id).then(yacht => {
            setReceiptNumber(yacht?.next_receipt ?? 1);
        }).catch(err => console.error(err.message));
    }, [currentSelectedYachtState, lastYachtRefreshTS]);

    useEffect(() => {
        if (!currentSelectedYachtState || !currentSelectedUserState) {
            return;
        }
        setData({
            ...receiptData,
            message_id: `RCPT.${currentSelectedYachtState.name}.${String(currentSelectedUserState.id).padStart(5, '0')}.${String(receiptNumber).padStart(6, '0')}`
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedYachtState, currentSelectedUserState, receiptNumber]);

    useEffect(() => {
        if (currentSelectedYachtState && sendStatus === 'success') {
            ApiCalls.incYachtReceiptCounter({
                gsheet_id: currentSelectedYachtState.gsheet_id,
                next_receipt: receiptNumber + 1,
            } as IYachtData).then(res => {
                toast.success('Receipt uploaded successfully.');
                if (res === null) {
                    console.error('Faild to increment rcpt number');
                }
                handleDone();
            }).catch(err => {
                console.error(err);
                if (onError) {
                    onError();
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSelectedYachtState, receiptNumber, sendStatus]);

    const handleNext = () => {
        if (step === 1) {
            if (!receiptData.amount || !receiptData.currency || !receiptData.vendor_name || receiptData.maris_category_id === -1 || !receiptData.user_description) {
                toast.error("Please fill in all the fields.");
                return;
            }
            setStep(2);
        } else if (step === 2) {
            if (sigCanvas.current?.isEmpty() && !signature) {
                toast.error("Please provide a signature.");
                return;
            }
            if (!signature && sigCanvas.current) {
                setSignature(sigCanvas.current.getCanvas().toDataURL("image/png"));
            }
            setStep(3);
        }
    };

    const handleBack = () => {
        if (step !== 1) {
            setStep(step - 1);
        }
    };

    const handleClearSignature = () => {
        sigCanvas.current?.clear();
        setSignature(null);
    };

    const captureReceiptAsJpg = async (): Promise<string | null> => {
        if (!receiptRef.current) return null;
            const canvas = await html2canvas(receiptRef.current, {
            scale: 2,
            backgroundColor: "#ffffff",
            logging: false,
            useCORS: true,
        });
        return canvas.toDataURL("image/jpeg", 0.9);
    };

    const handleSend = async () => {
        setSending(true);
        setSendStatus("idle");
        try {
            const jpgData = await captureReceiptAsJpg();
            if (!jpgData) throw new Error("Could not capture receipt");

            const uploadResponse = await ApiCalls.uploadFileToSupabase(jpgData, receiptData.message_id + '.jpg')
            console.log(uploadResponse);
            if (!uploadResponse || !uploadResponse.success) {
                console.error(uploadResponse);
                toast.error('Error uploading receipt!');
                return;
            }

            const createReceiptResponse = await ApiCalls.createReceipt({
                ...receiptData,
                supabase_image_link: uploadResponse.file_path
            });

            if (!createReceiptResponse) {
                const err = await createReceiptResponse.json();
                throw new Error(err.message || "Upload failed");
            }

            setSendStatus("success");
        } catch (err: any) {
            console.error("Send failed:", err);
            setSendStatus("error");
            toast.error("Failed to send receipt: " + (err.message || "Unknown error"));
        } finally {
            setSending(false);
        }
    };

    const handleDone = async () => {
        setSendStatus('idle');
        setSending(false);
        setSignature(null);
        setData(emptyReceipt);
        setStep(1);
        setLastYachtRefreshTS(Date.now());
        if (onSuccess) {
            onSuccess();
        }
    };

    const categoryOptions = GlobalPermissions.UX.EXPENSES.BoatCash.AllCategories.includes(`${isPettyCash ? '$' : ''}${ReduxUpdater.authenticatedUser?.user_role}`)
        ? GlobalVariables.accountingCategoriesFiltered(null)
        : GlobalConstants.CreateReceiptCategories;

    if (ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT) {
        const now = new Date();
        now.setDate(40);
        now.setDate(0);
        const prev = new Date();
        prev.setDate(0);
        prev.setDate(1);
        prev.setDate(0);
        prev.setDate(1);

        GlobalVariables.dialogConfig.dateMax = now;
        GlobalVariables.dialogConfig.dateMin = prev;
    }

    return (
        <div className="w-full max-w-xl mx-auto p-4 overflow-y-auto">
            <div className="mb-2 flex items-center justify-between">
                {/* <button
                    // variant="ghost"
                    onClick={handleBack}
                    data-testid="button-back-dashboard"
                    className="text-neutral-500"
                >
                    <ArrowLeft className="mr-2 w-4 h-4" /> {step === 1 ? "Dashboard" : ""}
                </button> */}
                <div className="w-24" />
                <div className="flex items-center gap-2">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                                step === i ? "bg-neutral-900 text-white" :
                                step > i ? "bg-neutral-200 text-neutral-900" : "bg-neutral-100 text-neutral-400"
                            }`}>
                                {step > i ? <CheckCircle2 className="w-4 h-4" /> : i}
                            </div>
                            {i < 3 && <div className={`w-8 h-1 rounded-full ${step > i ? "bg-neutral-200" : "bg-neutral-100"}`} />}
                        </div>
                    ))}
                </div>
                <div className="w-24" />
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="p-3 sm:p-4">
                            <div className="space-y-6 overflow-y-auto">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <h2 className="text-xl font-semibold mb-1">Receipt Details</h2>
                                        <p className="text-sm text-neutral-500">Enter the transaction information.</p>
                                    </div>
                                    {/* {ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT && !GlobalVariables.manualReceipt?.user_id && */}
                                        <SelectInput
                                            idx="creditCard"
                                            type="locked"
                                            label="Receipt Owner"
                                            value={((ReduxUpdater.authenticatedUser?.user_role === GlobalConstants.UserRole.ROOT ? receiptData.user_id : ReduxUpdater.authenticatedUser?.id) ?? -1).toString()}
                                            options={ReduxUpdater.yachtCrewCards.map((c, i) => ({
                                                label: `${c.first_name} ${c.last_name[0]}.`,
                                                value: c.id.toString(),
                                                sort: i
                                            })).sort((a, b) => a.sort - b.sort)}
                                            onChange={(newUserId) => {
                                                if (receiptData.user_id !== parseInt(newUserId)) {
                                                    setData({...receiptData, user_id: parseInt(newUserId)});
                                                }
                                            }}
                                        />
                                    {/* }  */}
                                </div>
                                <div className="grid grid-cols-1 overflow-y-auto gap-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <DateTimeInput
                                            type={
                                                !GlobalVariables.manualReceipt || !GlobalVariables.manualReceipt.transaction_date
                                                    ? (!receiptData.transaction_date ? 'warning' : 'edit')
                                                    : 'locked'
                                            }
                                            label="Date"
                                            width="[-webkit-fill-available]"
                                            receipt={receiptData}
                                            onUpdate={(newDate) => {
                                                if (receiptData.transaction_date !== newDate) {
                                                    setData({...receiptData, transaction_date: newDate});
                                                }
                                            }}
                                        />
                                        <TextInput
                                            idx="Recipient"
                                            label="Recipient"
                                            type={
                                                !GlobalVariables.manualReceipt || !GlobalVariables.manualReceipt.vendor_name
                                                    ? (!receiptData.vendor_name ? 'empty' : 'edit')
                                                    : 'locked'
                                            }
                                            value={receiptData.vendor_name}
                                            onChange={(newValue) => {
                                                if (receiptData.vendor_name !== newValue) {
                                                    setData({...receiptData, vendor_name: newValue});
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-4">
                                        <TextInput
                                            idx="Amount"
                                            label="Amount"
                                            type={
                                                !GlobalVariables.manualReceipt || !GlobalVariables.manualReceipt.amount
                                                    ? (!receiptData.amount ? 'empty' : 'edit')
                                                    : 'locked'
                                            }
                                            classes="col-span-2"
                                            value={receiptData.amount}
                                            onChange={(newValue) => {
                                                if (receiptData.amount !== newValue) {
                                                    setData({...receiptData, amount: newValue});
                                                }
                                            }}
                                        />
                                        <SelectInput
                                            idx="currency"
                                            label="Currency"
                                            type={
                                                !GlobalVariables.manualReceipt || !GlobalVariables.manualReceipt.currency
                                                    ? (!receiptData.currency ? 'empty' : 'edit')
                                                    : 'locked'
                                            }
                                            classes="col-span-1"
                                            value={receiptData.currency}
                                            options={currentSelectedYachtCurrencyState.map(c => ({
                                                label: c,
                                                value: c
                                            }))}
                                            onChange={(newValue) => {
                                                if (receiptData.currency !== newValue) {
                                                    setData({...receiptData, currency: newValue});
                                                }
                                            }}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <SelectInput
                                            idx="category"
                                            type={receiptData.maris_category_id === -1 ? 'empty' : 'edit'}
                                            value={(receiptData.maris_category_id ?? -1).toString()}
                                            options={categoryOptions}
                                            onChange={(newValue) => {
                                                if (receiptData.maris_category_id !== parseInt(newValue)) {
                                                    console.log('Updating maris_category_id', receiptData.maris_category_id, newValue);
                                                    setData({...receiptData, maris_category_id: parseInt(newValue)});
                                                }
                                            }}
                                        />
                                    </div>
                                    <TextInput
                                        idx="ShortDescription"
                                        label="Short Description"
                                        type={!receiptData.user_description ? 'empty' : 'edit'}
                                        value={receiptData.user_description ?? ''}
                                        onChange={(newValue) => {
                                            if (receiptData.user_description !== newValue) {
                                                setData({...receiptData, user_description: newValue});
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                            <div className={`mt-4 flex justify-${showCancel ? 'between' : 'end'}`}>
                                {showCancel && <button onClick={handleDone} className={`w-40 h-10 px-3 bg-buttons-invert rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                    <div className="flex-1 text-center justify-start text-text-dark text-sm font-semibold font-['Inter'] leading-tight">Cancel <X className="mr-2 w-4 h-4 inline" /></div>
                                </button>}
                                <button onClick={handleNext} className={`w-40 h-10 px-3 bg-buttons-primary rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                    <div className="flex-1 text-center justify-start text-text-dark-invert text-sm font-semibold font-['Inter'] leading-tight">Next Step <ArrowRight className="ml-2 w-4 h-4 inline" /></div>
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="p-3 sm:p-4">
                            <div className="space-y-6">
                                <div>
                                <h2 className="text-xl font-semibold mb-1">Recipient signature</h2>
                                <p className="text-sm text-neutral-500">Please sign to confirm the receipt of funds.</p>
                                </div>
                                <div className="border-2 border-dashed border-neutral-200 rounded-xl bg-neutral-50/50 overflow-hidden relative touch-none">
                                <SignatureCanvas
                                    ref={sigCanvas}
                                    canvasProps={{
                                        className: "w-full h-64 cursor-crosshair",
                                        // data-testid: "canvas-signature"
                                    }}
                                    backgroundColor="transparent"
                                    penColor="#000000"
                                    onEnd={() => { if (sigCanvas.current) setSignature(sigCanvas.current.getCanvas().toDataURL("image/png")); }}
                                />
                                <div className="absolute top-4 right-4 pointer-events-none opacity-20"><PenTool className="w-8 h-8" /></div>
                                <div className="absolute bottom-4 left-0 w-full flex justify-center pointer-events-none"><div className="w-3/4 border-b border-neutral-300"></div></div>
                                </div>
                                <div className="flex justify-end">
                                <button data-variant="ghost" data-size="sm" onClick={handleClearSignature} data-testid="button-clear-signature">Clear Signature</button>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-between">
                                <button onClick={handleBack} className={`w-40 h-10 px-3 bg-buttons-invert rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                    <div className="flex-1 text-center justify-start text-text-dark text-sm font-semibold font-['Inter'] leading-tight"><ArrowLeft className="mr-2 w-4 h-4 inline" /> Back</div>
                                </button>
                                <button onClick={handleNext} className={`w-40 h-10 px-3 bg-buttons-primary rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                    <div className="flex-1 text-center justify-start text-text-dark-invert text-sm font-semibold font-['Inter'] leading-tight">Review <ArrowRight className="ml-2 w-4 h-4 inline" /></div>
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} className="p-3 sm:p-4 bg-neutral-100">
                            {/* <div className="flex justify-between items-center mb-6">
                                <div>
                                <h2 className="text-xl font-semibold mb-1">Preview</h2>
                                <p className="text-sm text-neutral-500">Review your receipt.</p>
                                </div>
                            </div> */}

                            <div className="flex justify-center pb-4">
                                <div
                                    ref={receiptRef}
                                    className="receipt-paper w-full max-w-sm p-8 pb-12 text-sm"
                                    style={{ fontFamily: "'JetBrains Mono', monospace", color: "#171717", backgroundColor: "#ffffff" }}
                                    data-testid="container-receipt-preview"
                                >
                                    <div style={{ textAlign: "center", marginBottom: "1rem", borderBottom: "2px dashed #171717", paddingBottom: "0.5rem" }}>
                                        <h2 style={{ fontSize: "1.5rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", color: "#171717", marginBottom: "0.25rem" }}>Receipt</h2>
                                        <p style={{ color: "#737373", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem" }}>{receiptData.message_id}</p>
                                        <p style={{ color: "#737373", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem" }}>MANUAL TRANSACTION</p>
                                    </div>
                                    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem", marginBottom: "0.5rem" }}>
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span style={{ color: "#737373" }}>DATE:</span>
                                            <span style={{ fontWeight: 600, color: "#171717" }}>{receiptData.transaction_date}</span>
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                                            <span style={{ color: "#737373" }}>RECIPIENT:</span>
                                            <span style={{ fontWeight: 600, color: "#171717", textAlign: "right", textTransform: "uppercase" }}>{receiptData.vendor_name}</span>
                                        </div>
                                        <div style={{ paddingTop: "0.5rem", borderTop: "1px solid #e5e5e5" }}>
                                            <span style={{ color: "#737373", display: "block", marginBottom: "0.25rem" }}>DESCRIPTION:</span>
                                            <p style={{ color: "#171717", whiteSpace: "pre-wrap" }}>{receiptData.user_description || "N/A"}</p>
                                        </div>
                                    </div>
                                    <div style={{ borderTop: "2px solid #171717", padding: "0.5rem 1rem", margin: "0.5rem -1rem", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#fafafa" }}>
                                        <span style={{ fontWeight: 700, color: "#171717" }}>TOTAL</span>
                                        <span style={{ fontWeight: 700, fontSize: "1.25rem", color: "#171717" }}>{receiptData.currency} {parseFloat(receiptData.amount || "0").toFixed(2)}</span>
                                    </div>
                                    <div style={{ marginTop: "0.5rem", textAlign: "center" }}>
                                        {signature ? (
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                <img src={signature} alt="Signature" style={{ height: "5rem", objectFit: "contain", marginBottom: "0.5rem" }} />
                                                <div style={{ width: "12rem", borderBottom: "1px solid #a3a3a3", marginBottom: "0.5rem" }}></div>
                                                <span style={{ fontSize: "0.75rem", color: "#737373", textTransform: "uppercase", letterSpacing: "0.1em" }}>Recipient Signature</span>
                                            </div>
                                        ) : (
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                                <div style={{ height: "5rem" }}></div>
                                                <div style={{ width: "12rem", borderBottom: "1px solid #a3a3a3", marginBottom: "0.5rem" }}></div>
                                                <span style={{ fontSize: "0.75rem", color: "#737373", textTransform: "uppercase", letterSpacing: "0.1em" }}>Recipient Signature</span>
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ marginTop: "2rem", textAlign: "center", fontSize: "0.75rem", color: "#a3a3a3" }}>Generated securely by Maris Receipt Maker</div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 mt-1">
                                <div className="flex justify-between items-center">
                                    <button onClick={handleBack} disabled={sendStatus === "success"} className={`w-40 h-10 px-3 bg-buttons-invert rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                        <div className="flex-1 text-center justify-start text-text-dark text-sm font-semibold font-['Inter'] leading-tight"><ArrowLeft className="mr-2 w-4 h-4 inline" /> Edit</div>
                                    </button>
                                    <button onClick={handleSend} disabled={sendStatus === "success"} className={`w-40 h-10 px-3 bg-buttons-primary rounded-lg flex justify-center items-center gap-2.5 disabled:opacity-50 disabled:pointer-events-none`}>
                                        <div className="flex-1 text-center justify-start text-text-dark-invert text-sm font-semibold font-['Inter'] leading-tight">
                                            {sending ? (<><Loader2 className="mr-2 w-5 h-5 animate-spin inline" /> Sending...</>) : sendStatus === "success" ? (<><CheckCircle2 className="mr-2 w-5 h-5 inline" /> Sent!</>) : (<><Send className="mr-2 w-5 h-5 inline" /> Send</>)}
                                        </div>
                                    </button>
                                </div>
                                {sendStatus === "success" && (
                                    <div className="flex flex-col items-end gap-2">
                                        <p className="text-sm text-green-600" data-testid="text-send-success">Receipt uploaded successfully.</p>
                                        <button data-variant="outline" data-size="sm" onClick={handleDone} data-testid="button-back-to-dashboard">Create another receipt</button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default CreateReceipt;